import { BulletList, AlertBox } from '../components/UIComponents';
import { ClipboardList, Globe2, Server, Globe, Zap, ShieldCheck } from 'lucide-react';

export function ExecutiveSummary() {
    return (
        <section className="doc-section scroll-mt-20" id="executive-summary">
            <div className="mb-3 pb-2 border-b border-white/10">
                <h2 className="text-xl font-bold flex items-center gap-2.5 text-gray-100">
                    <ClipboardList className="text-blue-500" size={22} />
                    1. Executive Summary
                </h2>
                <p className="text-[14px] text-gray-200 mt-1.5 leading-relaxed">
                    OvulScanner is a network and infrastructure monitoring tool that gives you clear visibility into every device, network segment, and endpoint in your IT environment. It brings together network discovery, asset inventory, topology mapping, endpoint monitoring, firewall compliance, camera health, event management, and a data center digital twin into a single, unified interface.
                </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-1 gap-4">
                <div className="bg-gradient-to-br from-gray-800 to-gray-800/50 border border-white/5 rounded-xl p-5">
                    <h3 className="text-[15px] font-bold text-gray-100 mb-3 flex items-center gap-2">
                        <Zap size={16} className="text-amber-400" /> Key Value Propositions
                    </h3>
                    <BulletList items={[
                        'Complete Visibility — Monitor everything from physical hardware to application-layer endpoints, regardless of device type or vendor.',
                        'Automated Topology — Automatically map out your network down to the port level, track dependencies, and identify the root causes of issues.',
                        'Data Center Digital Twin — View a live, interactive 3D model of your racks, devices, cameras, and cooling systems with environmental data.',
                        'Unified Monitoring — Track network devices, servers, endpoints, cameras, firewalls, PLCs, and sensors in one system.',
                        'Service Monitoring — Keep an eye on your critical services with minute-by-minute checks and graphs for CPU, memory, and network traffic.',
                        'Proactive Alerts — Set up custom alert thresholds and rules to receive notifications via multiple channels before issues impact users.',
                        'Firewall SIEM — Collect and analyze firewall logs centrally to detect threats, correlate events, and search through logs easily.',
                        'Compliance Tools — Automate firewall compliance scanning, generate audit trails, and schedule reports with Role-Based Access Control.',
                        'Easy Discovery — Quickly discover and inventory assets on your network using over 50 built-in protocols with very little setup.',
                        'Multi-Tenant Support — Monitor different client environments separately, keeping all data isolated and access restricted per tenant.'
                    ]} />
                </div>
                <div className="bg-gradient-to-br from-gray-800 to-gray-800/50 border border-white/5 rounded-xl p-5">
                    <h3 className="text-[15px] font-bold text-gray-100 mb-3 flex items-center gap-2">
                        <Globe size={16} className="text-sky-400" /> Deployment Model
                    </h3>
                    <p className="text-[13px] text-gray-200 leading-relaxed mb-4">
                        You can deploy OvulScanner on-premises to monitor your enterprise network. The Agent Gateway runs on your local network to handle polling, data collection, and processing at the edge. Meanwhile, the Central Management Server provides the main dashboard, analytics, and configuration tools.
                    </p>
                    <AlertBox type="note" icon={<ShieldCheck size={16} />}>
                        This setup keeps your data local, ensures fast monitoring, and keeps working even if your internet connection goes down.
                    </AlertBox>
                </div>
            </div>
            {/* <Screenshot
                src="/AI-Powered_Network_Topology.png"
                alt="AI-Powered Network Topology"
            /> */}
        </section>
    );
}

export function MultiTenantArchitecture() {
    return (
        <section className="doc-section scroll-mt-20 mt-12" id="multi-tenant">
            <div className="mb-3 pb-2 border-b border-white/10">
                <h2 className="text-xl font-bold flex items-center gap-2.5 text-gray-100">
                    <Globe2 className="text-cyan-500" size={22} />
                    2. Multi-Tenant Architecture
                </h2>
                <p className="text-[14px] text-gray-200 mt-1.5 leading-relaxed">
                    The platform is built to support multiple tenants from the start, making it easy for MSPs and large organizations to manage separate sites or clients.
                </p>
            </div>
            <div className="space-y-4">
                <AlertBox type="success" icon={<ShieldCheck size={16} />}>
                    <strong>Complete Data Isolation:</strong> All data is strictly isolated at the tenant level. No user can access data from a tenant they are not explicitly assigned to.
                </AlertBox>
                <div className="bg-gray-800 border border-white/5 rounded-xl p-5">
                    <h3 className="text-[15px] font-bold text-gray-100 mb-3">2.1 What Is a Tenant?</h3>
                    <p className="text-[13px] text-gray-200 leading-relaxed">
                        A tenant is a separate monitoring environment, like a specific client, business unit, or building. Every tenant gets its own dedicated:
                    </p>
                    <div className="grid grid-cols-2 lg:grid-cols-3 gap-2 mt-3">
                        {['Agent Gateway(s)', 'Asset Inventory', 'Events & Alerts', 'Topology Maps', 'Digital Twins', 'Reports', 'Compliance Data', 'User Access', 'Credentials & Polling'].map(item => (
                            <div key={item} className="px-3 py-1.5 bg-black/30 border border-white/5 rounded text-[12px] text-gray-300">
                                • {item}
                            </div>
                        ))}
                    </div>
                </div>
                <div className="bg-gray-800 border border-white/5 rounded-xl p-5">
                    <h3 className="text-[15px] font-bold text-gray-100 mb-3">2.2 Complete Data Isolation</h3>
                    <BulletList items={[
                        'A user assigned to Client A cannot see any devices, alerts, reports, or settings for Client B.',
                        'Tenant data is always kept separate and is never mixed or shared.',
                        'Credentials, network configurations, audit logs, and rules remain strictly isolated to each tenant.',
                        'You can monitor multiple tenants centrally without sharing confidential data between them.'
                    ]} />
                </div>
                <div className="bg-gray-800 border border-white/5 rounded-xl p-5">
                    <h3 className="text-[15px] font-bold text-gray-100 mb-3">2.3 Tenant Assignment & User Access</h3>
                    <BulletList items={[
                        'Users must be assigned to tenants when their account is created; they cannot see unassigned tenants.',
                        'Super Admins have access to all tenants, Tenant Admins manage specific assigned tenants, and Tenant Users have restricted role-based access.',
                        'You can assign a single user to multiple tenants and give them different roles in each.'
                    ]} />
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-4">
                        <div className="p-3 bg-black/30 rounded border border-white/5">
                            <h4 className="text-[12px] font-bold text-cyan-400 mb-1">Super Admin</h4>
                            <p className="text-[11px] text-gray-300">Has full access across all tenants. Usually the main platform operator or service provider.</p>
                        </div>
                        <div className="p-3 bg-black/30 rounded border border-white/5">
                            <h4 className="text-[12px] font-bold text-cyan-400 mb-1">Tenant Admin</h4>
                            <p className="text-[11px] text-gray-300">Can manage users, settings, and data, but only for the specific tenants they are assigned to.</p>
                        </div>
                        <div className="p-3 bg-black/30 rounded border border-white/5">
                            <h4 className="text-[12px] font-bold text-cyan-400 mb-1">Tenant User</h4>
                            <p className="text-[11px] text-gray-300">Can only view or edit what their specific role allows within their assigned tenant.</p>
                        </div>
                        <div className="p-3 bg-black/30 rounded border border-white/5">
                            <h4 className="text-[12px] font-bold text-cyan-400 mb-1">Multi-Tenant User</h4>
                            <p className="text-[11px] text-gray-300">Uses a single account to access multiple tenants and can hold different roles in each.</p>
                        </div>
                    </div>
                </div>
                <div className="bg-gray-800 border border-white/5 rounded-xl p-5">
                    <h3 className="text-[15px] font-bold text-gray-100 mb-3">2.4 Monitoring Multiple Tenants from One Dashboard</h3>
                    <BulletList items={[
                        'Use the tenant switcher to quickly change your view for dashboards, maps, events, and reports.',
                        'You configure notification rules and integrations separately for each tenant.',
                        'Audit logs only track actions within that specific tenant to keep records clean and separated.'
                    ]} />
                </div>
                <div className="bg-gray-800 border border-white/5 rounded-xl p-5">
                    <h3 className="text-[15px] font-bold text-gray-100 mb-3">2.5 Deployment Scenarios</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        {[
                            ['Managed Service Provider', 'One platform monitors all client networks while engineers see only their assigned clients.'],
                            ['Enterprise Multiple Sites', 'Head office, regional offices, and plants are separate tenants; site teams see only their site while corporate IT views all.'],
                            ['Enterprise Business Units', 'Each division acts as its own tenant with local administration and central IT oversight.'],
                            ['Multi-Branch Retail or Industrial', 'Each branch is a tenant while the NOC monitors all branches centrally.']
                        ].map(([title, body]) => (
                            <div key={title} className="p-3 bg-black/30 rounded border border-white/5">
                                <h4 className="text-[12px] font-bold text-cyan-400 mb-1">{title}</h4>
                                <p className="text-[11px] text-gray-300">{body}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </section>
    );
}

export function PlatformArchitecture() {
    return (
        <section className="doc-section scroll-mt-20 mt-12" id="platform-architecture">
            <div className="mb-3 pb-2 border-b border-white/10">
                <h2 className="text-xl font-bold flex items-center gap-2.5 text-gray-100">
                    <Server className="text-purple-500" size={22} />
                    3. Platform Architecture Overview
                </h2>
                <p className="text-[14px] text-gray-200 mt-1.5 leading-relaxed">
                    OvulScanner breaks down its monitoring tasks across three main parts: the Agent Gateway, the Central Management Server, and lightweight Endpoint Agents. All parts communicate securely with end-to-end encryption.
                </p>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mt-4 text-center">
                <div className="p-5 border border-white/5 bg-gray-800 rounded-xl">
                    <div className="w-10 h-10 bg-orange-500/10 rounded-full flex items-center justify-center mx-auto mb-3 text-orange-400 font-bold">1</div>
                    <h4 className="text-[14px] font-bold text-gray-100 mb-2">Agent Gateway</h4>
                    <p className="text-[12px] text-gray-300">Edge collector performing automated discovery and local buffering.</p>
                    <div className="mt-3 text-left">
                        <BulletList items={[
                            'Handles asset discovery, metric polling, camera frame capturing, and sending data back to the server securely.',
                            'Can buffer data locally if the connection drops, and syncs automatically when reconnected.',
                            'Supports many protocols like SNMP, ICMP, SSH, and various APIs to collect data without agents.'
                        ]} />
                    </div>
                </div>
                <div className="p-5 border border-white/5 bg-gray-800 rounded-xl">
                    <div className="w-10 h-10 bg-blue-500/10 rounded-full flex items-center justify-center mx-auto mb-3 text-blue-400 font-bold">2</div>
                    <h4 className="text-[14px] font-bold text-gray-100 mb-2">Central Server</h4>
                    <p className="text-[12px] text-gray-300">Unified dashboard, AI analytics, and digital twin rendering engine.</p>
                    <div className="mt-3 text-left">
                        <BulletList items={[
                            'Provides your dashboard, alerts, reporting tools, and configuration settings.',
                            'Uses algorithms to build network topology maps, understand system dependencies, and detect issues.',
                            'Acts as the hub for integrations with Microsoft Teams, Slack, Email, WhatsApp, Webhooks, and ticketing systems.'
                        ]} />
                    </div>
                </div>
                <div className="p-5 border border-white/5 bg-gray-800 rounded-xl">
                    <div className="w-10 h-10 bg-emerald-500/10 rounded-full flex items-center justify-center mx-auto mb-3 text-emerald-400 font-bold">3</div>
                    <h4 className="text-[14px] font-bold text-gray-100 mb-2">Endpoint Agents</h4>
                    <p className="text-[12px] text-gray-300">Lightweight OS-level monitors for Windows, Linux, and macOS.</p>
                    <div className="mt-3 text-left">
                        <BulletList items={[
                            'Gathers server metrics, software installed, patches, running processes, Docker status, and logs.',
                            'Supports Windows, Mac, and Linux endpoints.',
                            'Encrypts all information before sending it to the Agent Gateway.'
                        ]} />
                    </div>
                </div>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-4">
                <div className="bg-gray-800 border border-white/5 rounded-xl p-5">
                    <h3 className="text-[14px] font-bold text-gray-100 mb-3">Data Flow</h3>
                    <BulletList items={[
                        'The gateway connects to your network and finds available devices.',
                        'Credentials are kept securely on the central server and sent to the gateway when needed.',
                        'The gateway schedules checks and talks to your devices to gather data.',
                        'Endpoint agents report their logs and metrics back to the gateway.',
                        'The gateway cleans up the data and securely encrypts it before sending it to the main server.',
                        'The server updates your maps, dashboards, and evaluates if any alerts should be sent.'
                    ]} />
                </div>
                <div className="bg-gray-800 border border-white/5 rounded-xl p-5">
                    <h3 className="text-[14px] font-bold text-gray-100 mb-3">Security & Encryption</h3>
                    <BulletList items={[
                        'We use modern TLS 1.2/1.3 encryption to protect all data as it travels across your network.',
                        'Passwords and keys are locked in a secure AES-256 vault when not in use.',
                        'We use SNMPv3 AuthPriv so that even your device polling is authenticated and encrypted.',
                        'Our agents do their job without needing full admin or root permissions.'
                    ]} />
                </div>
            </div>
        </section>
    );
}
